import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Spinner,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { LuCheck, LuPencil, LuX } from 'react-icons/lu'

interface Props {
  label: string
  icon: any
  value?: string | null
  placeholder?: string
  /** Called with the trimmed new value. Should throw / reject on failure. */
  onSave: (value: string) => Promise<void> | void
  /** Non-editable fields (e.g. email) still render the display but no pencil. */
  disabled?: boolean
  type?: 'text' | 'email' | 'tel'
  /** Placeholder shown in read mode when the value is empty. */
  emptyLabel?: string
  /** Optional formatter for the read-mode display (e.g. mask, normalise). */
  formatDisplay?: (v: string) => string
}

/**
 * Field with two modes:
 *   - read: value shown as text with a pencil icon; click anywhere on the
 *     row to enter edit mode.
 *   - edit: input focused, ⏎ / ✓ commits, ⎋ / × cancels. Spinner on save.
 *
 * onSave receives the trimmed value. If nothing changed vs the current value,
 * the commit is a no-op (no network call, no toast).
 */
export default function EditableTextField({
  label,
  icon,
  value,
  placeholder,
  onSave,
  disabled,
  type = 'text',
  emptyLabel = 'Non renseigné',
  formatDisplay,
}: Props) {
  const toast = useToast()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setDraft(value ?? '')
  }, [value])

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  const commit = async () => {
    const trimmed = draft.trim()
    const current = (value ?? '').trim()
    if (trimmed === current) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onSave(trimmed)
      setEditing(false)
    } catch (err: any) {
      toast({
        title: err?.message || 'Impossible de mettre à jour',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setSaving(false)
    }
  }

  const cancel = () => {
    setDraft(value ?? '')
    setEditing(false)
  }

  const display = value ? (formatDisplay ? formatDisplay(value) : value) : ''

  return (
    <Box>
      <Flex align="center" gap={2} mb={2}>
        <Icon as={icon} boxSize={4} color="gray.500" />
        <Text fontSize="sm" fontWeight="medium" color="gray.600">
          {label}
        </Text>
      </Flex>

      {editing && !disabled ? (
        <HStack spacing={2}>
          <Input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            type={type}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit()
              if (e.key === 'Escape') cancel()
            }}
            isDisabled={saving}
            borderColor="purple.300"
            _focus={{
              borderColor: 'purple.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
            }}
          />
          <IconButton
            aria-label="Enregistrer"
            icon={saving ? <Spinner size="sm" /> : <LuCheck />}
            colorScheme="purple"
            size="sm"
            onClick={commit}
            isDisabled={saving}
          />
          <IconButton
            aria-label="Annuler"
            icon={<LuX />}
            variant="ghost"
            size="sm"
            onClick={cancel}
            isDisabled={saving}
          />
        </HStack>
      ) : (
        <HStack
          spacing={2}
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
          bg={disabled ? 'gray.50' : 'white'}
          px={3}
          py={2}
          cursor={disabled ? 'default' : 'text'}
          _hover={disabled ? {} : { borderColor: 'purple.300', bg: 'purple.50' }}
          transition="all 0.15s ease"
          onClick={() => !disabled && setEditing(true)}
          role={disabled ? undefined : 'button'}
          minH="40px"
        >
          <Text
            flex={1}
            fontSize="sm"
            color={value ? 'gray.800' : 'gray.400'}
            fontStyle={value ? 'normal' : 'italic'}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {value ? display : emptyLabel}
          </Text>
          {!disabled && (
            <Tooltip label="Modifier" hasArrow openDelay={400}>
              <Box>
                <Icon as={LuPencil} boxSize={3.5} color="gray.400" />
              </Box>
            </Tooltip>
          )}
        </HStack>
      )}
    </Box>
  )
}
